import { Light } from './Light.js';
import { PointLight as BABYLON_PointLight } from '@inter-ikea-betrakta-babylonjs/core/Lights/pointLight.js';
import { vector_3 } from '../../../utility/Conversion.js';
import { TrackedVec3, degrees, radians } from '../../../../Math.js';
import { LightType } from '../../../../api.js';
import { PointLightVisualizer } from '../debug_visualizers/PointLightVisualizer.js';
import { SpotLightShadowController } from '../shadows/SpotLightShadowController.js';
import { calculate_direction_from_parent_world_transform, calculate_world_transform } from '../../../utility/TransformHelper.js';
import { Vector3 as BABYLON_Vector3 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
/**
 * DirectedShadowPointLight enables setting a direction for the shadow, effectivly making the shadow casting similar to that of a SpotLight,
 * while giving the same illumination as a PointLight. The shadow quality will be better due to support for percentage closer filtering (pcf).
 * The memory usage is lower since only one texture is needed instead of 6 for the regular PointLight.
 */
export class DirectedShadowPointLight extends Light {
    constructor(viewer, position = { x: 0, y: 0, z: 0 }) {
        super(viewer);
        this._direction = new TrackedVec3();
        this.type = LightType.DIRECTED_SHADOW_POINT_LIGHT;
        this._babylon_light = new BABYLON_PointLight(`Light_${this.id} (DirectedShadowPointLight)`, vector_3(position), this._viewer.scene);
        this.shadow = new SpotLightShadowController(this, this._babylon_light, this._light_system);
        // This is left undefined if we don't initialize it ourselves.
        this._babylon_light.direction = new BABYLON_Vector3(0, 0, 0);
        this._direction.onchange = () => this.mark_dirty(Light.DIRTY_DIRECTION);
        this._babylon_light.shadowAngle = Math.PI / 3;
        this.position = position;
    }
    get direction() {
        return this._direction;
    }
    set direction(value) {
        this._direction.set(value.x, value.y, value.z);
    }
    get shadow_angle_radians() {
        return this._babylon_light.shadowAngle;
    }
    set shadow_angle_radians(a) {
        if (this._babylon_light.shadowAngle === a)
            return;
        this._babylon_light.shadowAngle = a;
        this.shadow.validate_and_warn_shadow_angle();
        // Dirtying DIRTY_ANGLE might not have an effect today, but it does tell use the range of the light has changed, if useful in the future.
        this.mark_dirty(Light.DIRTY_ANGLE | Light.DIRTY_SHADOW_MAP);
    }
    get shadow_angle_degrees() {
        return degrees(this.shadow_angle_radians);
    }
    set shadow_angle_degrees(angle) {
        this.shadow_angle_radians = radians(angle);
    }
    update() {
        this.update_color_and_intensity();
        this.update_parent_material();
        if (this._parent) {
            const parent_updated = this.is_dirty(Light.DIRTY_PARENT_ADDED |
                Light.DIRTY_PARENT_TRANSFORM |
                Light.DIRTY_POSITION);
            if (parent_updated) {
                const world_position = this.get_world_position();
                const world_direction = this.get_world_direction();
                this._babylon_light.position.set(world_position.x, world_position.y, world_position.z);
                this._babylon_light.direction.set(world_direction.x, world_direction.y, world_direction.z);
                this.mark_dirty(Light.DIRTY_SHADOW_MAP);
            }
        }
        else {
            if (this.is_dirty(Light.DIRTY_POSITION)) {
                this._babylon_light.position.set(this.position.x, this.position.y, this.position.z);
                this.mark_dirty(Light.DIRTY_SHADOW_MAP);
            }
        }
        if (this.is_dirty(Light.DIRTY_DIRECTION)) {
            const world_direction = this.get_world_direction();
            this._babylon_light.direction.set(world_direction.x, world_direction.y, world_direction.z);
            this.mark_dirty(Light.DIRTY_SHADOW_MAP);
        }
    }
    get_world_direction() {
        if (!this._parent) {
            return this._direction;
        }
        const parent_world_transform = calculate_world_transform(this._parent);
        return calculate_direction_from_parent_world_transform(parent_world_transform, this._direction);
    }
    create_debug_geometry() {
        const { line_manager, scene, light_system } = this._viewer;
        this._debug_geometry = new PointLightVisualizer(this, line_manager, light_system.debug_color_mode, scene);
    }
}
//# sourceMappingURL=DirectedShadowPointLight.js.map